import { useEffect, useRef, useState } from "react";
import { Globe, Check, ChevronDown } from "lucide-react";

const LANGS = [
  { code: "en", label: "English" },
  { code: "es", label: "Español" },
  { code: "fr", label: "Français" },
  { code: "zh-CN", label: "简体中文" },
  { code: "ht", label: "Kreyòl ayisyen" },
  { code: "nl", label: "Nederlands" },
  { code: "ru", label: "Русский" },
  { code: "pt", label: "Português" },
  { code: "de", label: "Deutsch" },
  { code: "it", label: "Italiano" },
];

export default function LanguageSwitcher() {
  const [open, setOpen] = useState(false);
  const [current, setCurrent] = useState("en");
  const ref = useRef(null);

  useEffect(() => {
    window.googleTranslateElementInit = () => {
      try {
        new window.google.translate.TranslateElement({ pageLanguage: "en", autoDisplay: false }, "google_translate_element");
      } catch (e) { /* translate unavailable */ }
    };
    if (!document.getElementById("google-translate-script")) {
      const s = document.createElement("script");
      s.id = "google-translate-script";
      s.src = "https://translate.google.com/translate_a/element.js?cb=googleTranslateElementInit";
      document.body.appendChild(s);
    }
    const m = document.cookie.match(/googtrans=\/en\/([a-zA-Z-]+)/);
    if (m && LANGS.some((l) => l.code === m[1])) setCurrent(m[1]);

    const onClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const pick = (code) => {
    setCurrent(code);
    setOpen(false);
    const select = document.querySelector(".goog-te-combo");
    if (select) {
      select.value = code;
      select.dispatchEvent(new Event("change"));
    } else {
      document.cookie = `googtrans=/en/${code}; path=/`;
      document.cookie = `googtrans=/en/${code}; path=/; domain=${window.location.hostname}`;
      window.location.reload();
    }
  };

  const currentLabel = LANGS.find((l) => l.code === current)?.label || "English";

  return (
    <div ref={ref} className="relative" data-testid="language-switcher">
      <div id="google_translate_element" />
      <button
        data-testid="language-switcher-button"
        onClick={() => setOpen(!open)}
        aria-label="Change language"
        className="flex items-center gap-1.5 text-sm font-ui text-muted-foreground hover:text-primary transition-colors duration-200 whitespace-nowrap"
      >
        <Globe size={15} className="gold-text" />
        <span className="hidden lg:inline">{currentLabel}</span>
        <ChevronDown size={12} className={`transition-transform duration-200 ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div data-testid="language-menu" className="absolute right-0 top-full mt-3 w-48 bg-card border border-border rounded-2xl shadow-xl shadow-black/10 py-2 z-50 fade-up">
          {LANGS.map((l) => (
            <button
              key={l.code}
              data-testid={`language-option-${l.code}`}
              onClick={() => pick(l.code)}
              className={`w-full flex items-center justify-between px-4 py-2.5 text-sm font-ui text-left transition-colors duration-150 ${
                current === l.code ? "text-primary bg-primary/5" : "text-foreground/80 hover:bg-black/5 hover:text-primary"
              }`}
            >
              {l.label}
              {current === l.code && <Check size={14} className="gold-text" />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
